import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, CheckCircle, AlertCircle, Loader2, Database, Link, Search } from "lucide-react";
import { SpreadsheetGridFixed } from "./import-v2/SpreadsheetGridFixed";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { findIntelligentMatch, MatchResult, MatchingContext } from "./import-v2/matchingUtils";

interface KolDbEntry {
  id: string;
  creator_name: string;
  social_media_handler?: string;
  social_media_type?: string;
  tier?: string;
}

interface KolTrackerEntry {
  id: string;
  name: string;
  post_link?: string;
  channel?: string;
  kol_db_id?: string;
  tier?: string;
  language?: string;
}

interface GridRowData {
  [key: string]: string;
}

interface DataImportPageProps {
  onDataImported?: () => void;
}

export function DataImportPage({ onDataImported }: DataImportPageProps) {
  const [activeImportTab, setActiveImportTab] = useState<string>("kol_tracker");
  const [gridData, setGridData] = useState<GridRowData[]>([]);
  const [validationResults, setValidationResults] = useState<Record<number, { isValid: boolean; errors: string[]; warnings: string[]; nameCorrection?: string }>>({});
  const [matchResults, setMatchResults] = useState<Record<number, MatchResult>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [kolDbData, setKolDbData] = useState<KolDbEntry[]>([]);
  const [kolTrackerData, setKolTrackerData] = useState<KolTrackerEntry[]>([]);
  const [kolPicUserName, setKolPicUserName] = useState<string>("");
  const [isUserNameLoaded, setIsUserNameLoaded] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();

  const TABLE_COLUMNS = {
    kol_tracker: [
      { key: 'name', label: 'KOL Name', required: true },
      { key: 'post_type', label: 'KOL Type', required: false },
      { key: 'type', label: 'Deliverable', required: false },
      { key: 'post_link', label: 'Link', required: false },
      { key: 'posting_date', label: 'Date', required: false },
      { key: 'content_views', label: 'Views', required: false },
      { key: 'pic', label: 'PIC', required: false, readOnly: false },
      { key: 'matched_creator', label: 'Matched Creator', required: false, readOnly: false },
      { key: 'actions', label: 'Actions', required: false, readOnly: true }
    ],
    kol_db: [
      { key: 'creator_name', label: 'Creator Name', required: true },
      { key: 'social_media_handler', label: 'Handler', required: false },
      { key: 'social_media_type', label: 'Platform', required: false },
      { key: 'region', label: 'Region', required: false },
      { key: 'tier', label: 'Tier', required: false },
      { key: 'pic', label: 'PIC', required: false, readOnly: false },
      { key: 'actions', label: 'Actions', required: false, readOnly: true }
    ]
  };

  const normalizeHandler = useCallback((handler: string): string => {
    if (!handler) return '';
    const normalized = handler.toLowerCase().trim();
    return normalized.startsWith('@') ? normalized.substring(1) : normalized;
  }, []);

  const extractHandlerFromUrl = useCallback((url: string): { handler: string | null; platform: string | null } => {
    if (!url) return { handler: null, platform: null };
    
    console.log(`🔍 Extracting handler from URL: "${url}"`);
    
    const telegramPatterns = [
      /t\.me\/([^\/\?&#]+)/i,
      /telegram\.me\/([^\/\?&#]+)/i,
      /tg\.me\/([^\/\?&#]+)/i
    ];
    
    for (const pattern of telegramPatterns) {
      const match = url.match(pattern);
      if (match) {
        const handler = match[1];
        console.log(`📱 Telegram handler extracted: "${handler}"`);
        return { handler, platform: 'telegram' };
      }
    }
    
    const twitterPatterns = [
      /(?:x\.com|twitter\.com)\/([^\/\?&#]+)/i,
      /(?:mobile\.twitter\.com)\/([^\/\?&#]+)/i
    ];
    
    for (const pattern of twitterPatterns) {
      const match = url.match(pattern);
      if (match) {
        const handler = match[1];
        console.log(`🐦 Twitter handler extracted: "${handler}"`);
        return { handler, platform: 'twitter' };
      }
    }
    
    console.log(`❌ No handler found in URL: "${url}"`);
    return { handler: null, platform: null };
  }, []);

  const fetchKolPicUserName = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      const { data, error } = await supabase
        .from('kol_pic')
        .select('name')
        .eq('email', user.email)
        .single();
      
      if (error) {
        console.error("Error fetching user name from kol_pic:", error);
        setKolPicUserName(user.email || 'Unknown User');
        setIsUserNameLoaded(true);
        return;
      }
      
      setKolPicUserName(data?.name || user.email || 'Unknown User');
      setIsUserNameLoaded(true);
    } catch (error) {
      console.error("Error in fetchKolPicUserName:", error);
      setKolPicUserName(user.email || 'Unknown User');
      setIsUserNameLoaded(true);
    }
  }, [user?.email]);

  const fetchKolDbData = useCallback(async () => {
    try {
      console.log("🔄 Fetching KOL DB data...");
      const { data, error } = await supabase
        .from('kol_db')
        .select('id, creator_name, social_media_handler, social_media_type, tier');
      
      if (error) {
        console.error("❌ Error fetching KOL DB data:", error);
        throw error;
      }
      
      console.log(`✅ KOL DB data fetched: ${data?.length || 0} records`);
      if (data) setKolDbData(data);
    } catch (error) {
      console.error("Error fetching KOL DB data:", error);
      setKolDbData([]);
    }
  }, []);

  const fetchKolTrackerData = useCallback(async () => {
    try {
      console.log("🔄 Fetching KOL Tracker data...");
      const { data, error } = await supabase
        .from('kol_tracker')
        .select('id, name, post_link, channel, kol_db_id, tier, language');
      
      if (error) {
        console.error("❌ Error fetching KOL Tracker data:", error);
        throw error;
      }
      
      console.log(`✅ KOL Tracker data fetched: ${data?.length || 0} records`);
      if (data) setKolTrackerData(data);
    } catch (error) {
      console.error("Error fetching KOL Tracker data:", error);
      setKolTrackerData([]);
    }
  }, []);

  useEffect(() => {
    if (activeImportTab === 'kol_tracker') {
      const loadData = async () => {
        setIsDataLoaded(false);
        console.log("🚀 Loading KOL data for matching...");
        
        await Promise.all([
          fetchKolDbData(),
          fetchKolTrackerData()
        ]);
        
        setIsDataLoaded(true);
        console.log("✅ Data loading completed");
      };
      
      loadData();
    }
  }, [activeImportTab, fetchKolDbData, fetchKolTrackerData]);

  useEffect(() => {
    if (user?.email) {
      fetchKolPicUserName();
    }
  }, [user?.email, fetchKolPicUserName]);

  const findMatchingKolDbId = useCallback((kolName: string, postLink: string): MatchResult => {
    console.log(`\n🎯 === STARTING INTELLIGENT MATCHING ===`);
    console.log(`Input KOL Name: "${kolName}"`);
    console.log(`Input Post Link: "${postLink}"`);
    console.log(`Data loaded: ${isDataLoaded}`);
    console.log(`KOL Tracker records: ${kolTrackerData.length}`);
    console.log(`KOL DB records: ${kolDbData.length}`);
    
    if (!isDataLoaded || kolTrackerData.length === 0) {
      console.log(`❌ Data not ready for matching`);
      return {
        kolDbId: null,
        matchedCreator: 'No match found',
        confidence: 0,
        matchReason: 'Data not loaded',
        warnings: ['Matching data not available']
      };
    }
    
    const matchingContext: MatchingContext = {
      kolDbData,
      kolTrackerData
    };
    
    const result = findIntelligentMatch(kolName, postLink, matchingContext);
    console.log(`🎯 Intelligent match result:`, result);
    
    return result;
  }, [kolDbData, kolTrackerData, isDataLoaded]);

  const detectPlatformFromDeliverable = useCallback((deliverable: string): string | null => {
    if (!deliverable) return null;
    
    const lowerDeliverable = deliverable.toLowerCase().trim();
    
    if (lowerDeliverable.includes('twitter')) {
      return 'Twitter';
    } else if (lowerDeliverable.includes('telegram') || lowerDeliverable.includes('tg')) {
      return 'Telegram';
    } else if (lowerDeliverable.includes('youtube')) {
      return 'Youtube';
    } else if (lowerDeliverable.includes('instagram')) {
      return 'Instagram';
    } else if (lowerDeliverable.includes('tiktok')) {
      return 'TikTok';
    }
    
    return null;
  }, []);

  const validateRow = useCallback(async (rowIndex: number, rowData: GridRowData) => {
    console.log(`🔍 [Row ${rowIndex}] Manual validation started`);
    
    const errors: string[] = [];
    const warnings: string[] = [];
    let nameCorrection: string | undefined;
    
    if (activeImportTab === 'kol_tracker') {
      if (!rowData.name?.trim()) {
        errors.push("KOL Name is required");
      }
      
      const matchedCreator = rowData.matched_creator?.trim();
      const matchResult = matchResults[rowIndex];
      
      if (!matchedCreator || matchedCreator === 'No match found') {
        if (!matchResult || matchResult.matchReason !== 'Manual selection') {
          errors.push("No matching creator found - entry cannot be imported");
        }
      } else {
        if (matchResult && matchResult.matchReason !== 'Manual selection') {
          if (matchResult.warnings.length > 0) {
            warnings.push(...matchResult.warnings);
          }
        }
      }
      
      if (rowData.content_views?.trim()) {
        const viewsValue = parseFloat(rowData.content_views.trim());
        if (isNaN(viewsValue) || viewsValue < 0) {
          errors.push("Views must be a valid positive number");
        }
      }
      
      if (rowData.post_link?.trim()) {
        const postLink = rowData.post_link.trim();
        
        const duplicateInImportIndex = gridData.findIndex((row, index) => 
          index !== rowIndex && row.post_link?.trim() === postLink
        );
        
        if (duplicateInImportIndex !== -1) {
          errors.push(`Duplicate post link found in row #${duplicateInImportIndex + 1}`);
        } else {
          try {
            const { data, error } = await supabase
              .from('kol_tracker')
              .select('id')
              .eq('post_link', postLink)
              .limit(1);
              
            if (error) throw error;
            if (data && data.length > 0) {
              errors.push("Post link already exists in the database");
            }
          } catch (error) {
            console.error("Error checking duplicate:", error);
            warnings.push("Could not verify if post link is duplicate");
          }
        }
      }
      
      if (rowData.posting_date?.trim()) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(rowData.posting_date.trim())) {
          errors.push("Date must be in YYYY-MM-DD format");
        }
      }
    } else if (activeImportTab === 'kol_db') {
      if (!rowData.creator_name?.trim()) {
        errors.push("Creator Name is required");
      }
      
      if (rowData.creator_name?.trim()) {
        try {
          const { data, error } = await supabase
            .from('kol_db')
            .select('id')
            .eq('creator_name', rowData.creator_name.trim())
            .limit(1);
            
          if (error) throw error;
          if (data && data.length > 0) {
            warnings.push("Creator already exists in the system");
          }
        } catch (error) {
          console.error("Error checking duplicate:", error);
        }
      }
    }
    
    setValidationResults(prev => {
      const newResults = { ...prev };
      newResults[rowIndex] = {
        isValid: errors.length === 0,
        errors,
        warnings,
        nameCorrection
      };
      return newResults;
    });
    
    console.log(`✅ [Row ${rowIndex}] Manual validation completed - Valid: ${errors.length === 0}`);
  }, [activeImportTab, gridData, matchResults]);

  const handleTableSelect = (table: string) => {
    console.log("Switching destination to:", table);
    
    setActiveImportTab(table);
    setGridData([]);
    setValidationResults({});
    setMatchResults({});
    setIsDataLoaded(false);
    setIsValidated(false);
    
    if (isUserNameLoaded) {
      initializeGridData(table);
    }
  };

  const initializeGridData = (table: string) => {
    const columns = TABLE_COLUMNS[table as keyof typeof TABLE_COLUMNS];
    const singleRow: GridRowData = {};
    columns.forEach(col => {
      if (col.key === 'pic') {
        singleRow[col.key] = kolPicUserName || 'Unknown User';
      } else if (col.key === 'matched_creator') {
        singleRow[col.key] = '';
      } else if (col.key === 'actions') {
        singleRow[col.key] = '';
      } else {
        singleRow[col.key] = '';
      }
    });
    setGridData([singleRow]);
  };

  useEffect(() => {
    if (isUserNameLoaded && activeImportTab) {
      initializeGridData(activeImportTab);
    }
  }, [isUserNameLoaded, kolPicUserName]);

  useEffect(() => {
    if (isDataLoaded && gridData.length > 0 && activeImportTab === 'kol_tracker') {
      console.log("🔄 Data loaded, updating matches for existing grid data");
      
      const updatedData = [...gridData];
      const newMatchResults: Record<number, MatchResult> = { ...matchResults };
      
      updatedData.forEach((row, index) => {
        if (row.name || row.post_link) {
          const matchResult = findMatchingKolDbId(row.name || '', row.post_link || '');
          newMatchResults[index] = matchResult;
          
          if (matchResult.kolDbId) {
            const kolDbEntry = kolDbData.find(kol => kol.id === matchResult.kolDbId);
            if (kolDbEntry) {
              row.matched_creator = `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})`;
              
              row._kolDbId = matchResult.kolDbId;
              row._tier = matchResult.tier || kolDbEntry.tier || '';
              row._language = matchResult.language || '';
              row._channel = matchResult.channel || '';
            }
          } else {
            row.matched_creator = 'No match found';
          }
        }
      });
      
      setMatchResults(newMatchResults);
      setGridData(updatedData);
    }
  }, [isDataLoaded, findMatchingKolDbId, activeImportTab, kolDbData]);

  const handleGridDataChange = useCallback((newData: GridRowData[]) => {
    console.log('🔄 Grid data changed:', newData);
    
    // Reset validation state when data changes
    setIsValidated(false);
    
    if (!isDataLoaded && activeImportTab === 'kol_tracker') {
      console.log('⏳ Data not loaded yet, setting grid data without matching');
      setGridData(newData);
      return;
    }
    
    if (activeImportTab === 'kol_tracker') {
      const updatedData = [...newData];
      const newMatchResults: Record<number, MatchResult> = {};
      
      updatedData.forEach((row, index) => {
        if (row.name || row.post_link) {
          const matchResult = findMatchingKolDbId(row.name || '', row.post_link || '');
          newMatchResults[index] = matchResult;
          
          if (matchResult.kolDbId) {
            const kolDbEntry = kolDbData.find(kol => kol.id === matchResult.kolDbId);
            if (kolDbEntry) {
              row.matched_creator = `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})`;
              
              row._kolDbId = matchResult.kolDbId;
              row._tier = matchResult.tier || kolDbEntry.tier || '';
              row._language = matchResult.language || '';
              row._channel = matchResult.channel || '';
            }
          } else {
            row.matched_creator = 'No match found';
          }
        } else {
          row.matched_creator = row.matched_creator || '';
        }
      });
      
      setMatchResults(newMatchResults);
      setGridData(updatedData);
      
      newData.forEach((rowData, index) => {
        const hasData = Object.values(rowData).some(value => value?.trim() !== '');
        if (!hasData) {
          setValidationResults(prev => {
            const newResults = { ...prev };
            delete newResults[index];
            return newResults;
          });
        }
      });
    } else {
      setGridData(newData);
      
      newData.forEach((rowData, index) => {
        const hasData = Object.values(rowData).some(value => value?.trim() !== '');
        if (!hasData) {
          setValidationResults(prev => {
            const newResults = { ...prev };
            delete newResults[index];
            return newResults;
          });
        }
      });
    }
  }, [validateRow, activeImportTab, findMatchingKolDbId, isDataLoaded, kolDbData, gridData]);

  const handleManualMatch = useCallback((rowIndex: number, kolDbId: string, extractedHandler: string) => {
    console.log(`🎯 Manual match for row ${rowIndex}: kolDbId=${kolDbId}, handler=${extractedHandler}`);
    
    // Reset validation state and clear all validation results when manual matching occurs
    setIsValidated(false);
    setValidationResults({});
    
    const updatedData = [...gridData];
    const kolDbEntry = kolDbData.find(kol => kol.id === kolDbId);
    
    if (kolDbEntry) {
      updatedData[rowIndex].matched_creator = `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})`;
      
      updatedData[rowIndex]._kolDbId = kolDbId;
      updatedData[rowIndex]._tier = kolDbEntry.tier || '';
      updatedData[rowIndex]._language = '';
      updatedData[rowIndex]._channel = extractedHandler || kolDbEntry.social_media_handler || '';
      
      const newMatchResults = { ...matchResults };
      newMatchResults[rowIndex] = {
        kolDbId: kolDbId,
        matchedCreator: kolDbEntry.creator_name,
        confidence: 100,
        matchReason: 'Manual selection',
        warnings: [],
        tier: kolDbEntry.tier,
        language: '',
        channel: extractedHandler || kolDbEntry.social_media_handler || ''
      };
      
      setMatchResults(newMatchResults);
      setGridData(updatedData);
      
      toast({
        title: "Manual Match Applied",
        description: `Row ${rowIndex + 1} matched with ${kolDbEntry.creator_name}. Please validate all data again.`,
      });
    }
  }, [gridData, kolDbData, matchResults, toast]);

  const handleDeleteRow = useCallback((rowIndex: number) => {
    const newData = gridData.filter((_, index) => index !== rowIndex);
    setGridData(newData);
    
    setValidationResults(prev => {
      const newResults = { ...prev };
      delete newResults[rowIndex];
      const reindexedResults: typeof newResults = {};
      Object.entries(newResults).forEach(([key, value]) => {
        const oldIndex = parseInt(key);
        if (oldIndex > rowIndex) {
          reindexedResults[oldIndex - 1] = value;
        } else if (oldIndex < rowIndex) {
          reindexedResults[oldIndex] = value;
        }
      });
      return reindexedResults;
    });
    
    setMatchResults(prev => {
      const newResults = { ...prev };
      delete newResults[rowIndex];
      const reindexedResults: typeof newResults = {};
      Object.entries(newResults).forEach(([key, value]) => {
        const oldIndex = parseInt(key);
        if (oldIndex > rowIndex) {
          reindexedResults[oldIndex - 1] = value;
        } else if (oldIndex < rowIndex) {
          reindexedResults[oldIndex] = value;
        }
      });
      return reindexedResults;
    });
  }, [gridData]);

  const handleImport = async () => {
    const validRows = gridData.filter((row, index) => {
      const hasData = Object.values(row).some(value => value?.trim() !== '');
      const validation = validationResults[index];
      return hasData && validation?.isValid !== false;
    });

    console.log(`Total rows in grid: ${gridData.length}`);
    console.log(`Valid rows for import: ${validRows.length}`);

    if (validRows.length === 0) {
      toast({
        title: "No Valid Data",
        description: "Please add valid data to import",
        variant: "destructive"
      });
      return;
    }

    console.log(`Starting import for ${activeImportTab} with ${validRows.length} rows`);
    setIsImporting(true);
    
    try {
      let successful = 0;
      const failed: any[] = [];
      
      for (const [index, row] of validRows.entries()) {
        try {
          console.log(`Processing row ${index + 1}/${validRows.length}:`, row);
          
          if (activeImportTab === 'kol_tracker') {
            let kolDbId = row._kolDbId || null;
            let actualKolName = row.name;
            let tier = row._tier || null;
            let language = row._language || null;
            let channel = row._channel || null;
            
            if (!kolDbId && (actualKolName?.trim() || row.post_link?.trim())) {
              const matchResult = findMatchingKolDbId(actualKolName || '', row.post_link || '');
              kolDbId = matchResult.kolDbId;
              tier = matchResult.tier || null;
              language = matchResult.language || null;
              channel = matchResult.channel || null;
              
              if (matchResult.kolDbId) {
                const kolDbEntry = kolDbData.find(kol => kol.id === matchResult.kolDbId);
                if (kolDbEntry) {
                  actualKolName = kolDbEntry.creator_name;
                  tier = tier || kolDbEntry.tier || null;
                }
              }
              
              console.log(`Match result for row ${index + 1}:`, matchResult);
            }
            
            const detectedPlatform = detectPlatformFromDeliverable(row.type || '');
            console.log(`Detected platform for row ${index + 1}: ${detectedPlatform}`);
            
            let picArray = [kolPicUserName || 'Unknown User'];
            if (row.pic && row.pic.trim()) {
              const picValue = row.pic.trim();
              picArray = picValue.split(',').map(p => p.trim()).filter(p => p.length > 0);
            }
            
            const insertData = {
              name: actualKolName?.trim() || null,
              post_type: row.post_type?.trim() || null,
              type: row.type?.trim() || null,
              post_link: row.post_link?.trim() || null,
              posting_date: row.posting_date?.trim() || null,
              content_views: row.content_views?.trim() ? parseFloat(row.content_views.trim()) : null,
              kol_db_id: kolDbId,
              pic: picArray,
              campaign_bu: [],
              integration_enable: true,
              platform: detectedPlatform,
              tier: tier,
              language: language,
              channel: channel
            };
            
            console.log(`Inserting into kol_tracker:`, insertData);
            
            const { data: insertedData, error } = await supabase
              .from('kol_tracker')
              .insert(insertData)
              .select();
              
            if (error) {
              console.error(`Error inserting row ${index + 1}:`, error);
              throw error;
            }
            
            console.log(`Successfully inserted row ${index + 1}:`, insertedData);
            successful++;
            
          } else if (activeImportTab === 'kol_db') {
            if (!row.creator_name || !row.creator_name.trim()) {
              throw new Error('Creator name is required');
            }
            
            let picArray = [kolPicUserName || 'Unknown User'];
            if (row.pic && row.pic.trim()) {
              const picValue = row.pic.trim();
              picArray = picValue.split(',').map(p => p.trim()).filter(p => p.length > 0);
            }
            
            const insertData = {
              creator_name: row.creator_name.trim(),
              social_media_handler: row.social_media_handler?.trim() || null,
              social_media_type: row.social_media_type?.trim() || null,
              region: row.region?.trim() || null,
              tier: row.tier?.trim() || null,
              pic_id: picArray
            };
            
            console.log(`Inserting into kol_db:`, insertData);
            
            const { data: existingCreator, error: checkError } = await supabase
              .from('kol_db')
              .select('id, creator_name')
              .eq('creator_name', insertData.creator_name)
              .maybeSingle();
            
            if (checkError) {
              console.error(`Error checking for duplicate creator:`, checkError);
              throw new Error(`Database error: ${checkError.message}`);
            }
            
            if (existingCreator) {
              console.warn(`Creator "${insertData.creator_name}" already exists with ID: ${existingCreator.id}`);
              throw new Error(`Creator "${insertData.creator_name}" already exists in the database`);
            }
            
            const { data: insertedData, error } = await supabase
              .from('kol_db')
              .insert(insertData)
              .select();
              
            if (error) {
              console.error(`Error inserting KOL DB row ${index + 1}:`, error);
              throw new Error(`Database error: ${error.message}`);
            }
            
            if (!insertedData || insertedData.length === 0) {
              throw new Error('Insert succeeded but no data was returned');
            }
            
            console.log(`Successfully inserted KOL DB row ${index + 1}:`, insertedData[0]);
            successful++;
          }
        } catch (error) {
          console.error(`Failed to process row ${index + 1}:`, error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          failed.push({ 
            row, 
            error: errorMessage,
            rowNumber: index + 1
          });
        }
      }
      
      console.log(`Import completed. Successful: ${successful}, Failed: ${failed.length}`);
      
      if (failed.length === 0) {
        toast({
          title: "Import Successful",
          description: `Successfully imported ${successful} records to ${activeImportTab === 'kol_db' ? 'KOL Dictionary' : 'KOL Posting Links'}`,
          variant: "default"
        });
      } else if (successful > 0) {
        toast({
          title: "Partial Import Complete",
          description: `Successfully imported ${successful} records, ${failed.length} failed. Check console for details.`,
          variant: "destructive"
        });
        
        console.group('Failed Import Rows:');
        failed.forEach(failure => {
          console.error(`Row ${failure.rowNumber}:`, failure.error);
          console.log('Row data:', failure.row);
        });
        console.groupEnd();
      } else {
        toast({
          title: "Import Failed",
          description: `All ${failed.length} records failed to import. Check console for details.`,
          variant: "destructive"
        });
        
        console.group('All Import Failures:');
        failed.forEach(failure => {
          console.error(`Row ${failure.rowNumber}:`, failure.error);
          console.log('Row data:', failure.row);
        });
        console.groupEnd();
      }
      
      if (onDataImported && successful > 0) {
        onDataImported();
      }
      
      if (successful > 0) {
        handleTableSelect(activeImportTab);
      }
      
    } catch (error) {
      console.error("Critical import error:", error);
      toast({
        title: "Import Failed",
        description: `Critical error during import: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleValidationTrigger = useCallback((rowIndex: number) => {
    const rowData = gridData[rowIndex];
    if (rowData) {
      console.log(`🔄 [Row ${rowIndex}] Manual validation triggered from DataImportPage`);
      validateRow(rowIndex, rowData);
    }
  }, [gridData, validateRow]);

  const hasDataToValidate = useMemo(() => {
    return gridData.some(row => 
      Object.values(row).some(value => value?.trim() !== '' && value?.trim() !== 'No match found')
    );
  }, [gridData]);

  const hasUnvalidatedRows = useMemo(() => {
    return gridData.some((row, index) => {
      const hasData = Object.values(row).some(value => value?.trim() !== '' && value?.trim() !== 'No match found');
      return hasData && !validationResults[index];
    });
  }, [gridData, validationResults]);

  const handleValidateAll = useCallback(async () => {
    console.log('🔄 Starting manual validation for all rows with data');
    
    const rowsToValidate = gridData
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => 
        Object.values(row).some(value => value?.trim() !== '' && value?.trim() !== 'No match found')
      );

    console.log(`Found ${rowsToValidate.length} rows to validate`);

    for (const { row, index } of rowsToValidate) {
      console.log(`Validating row ${index + 1}:`, row);
      await validateRow(index, row);
    }

    // Mark as validated after all rows are processed
    setIsValidated(true);

    toast({
      title: "Validation Complete",
      description: `Validated ${rowsToValidate.length} rows`,
    });
  }, [gridData, validateRow, toast]);

  const columns = TABLE_COLUMNS[activeImportTab as keyof typeof TABLE_COLUMNS] || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-card px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Upload className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Data Import Center</h1>
                <p className="text-sm text-muted-foreground">
                  Import data with intelligent matching and validation
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {hasDataToValidate && (hasUnvalidatedRows || !isValidated) && (
              <Button 
                onClick={handleValidateAll}
                variant="outline"
                size="sm"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Search className="h-4 w-4 mr-2" />
                Validate Imports
              </Button>
            )}
            
            <Button 
              onClick={handleImport}
              disabled={isImporting || gridData.length === 0 || !isValidated || hasUnvalidatedRows}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
              size="sm"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Import {Object.values(validationResults).filter(v => v.isValid).length} Rows
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="flex flex-col">
          <div className="bg-muted/30 px-6 py-3 border-b">
            <Tabs value={activeImportTab} onValueChange={handleTableSelect} className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-sm">
                <TabsTrigger value="kol_tracker" className="flex items-center gap-2 text-xs">
                  <Link className="h-3 w-3" />
                  KOL Posting Links
                </TabsTrigger>
                <TabsTrigger value="kol_db" className="flex items-center gap-2 text-xs">
                  <Database className="h-3 w-3" />
                  KOL Dictionary
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="bg-muted/10 px-6 py-2 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">
                {activeImportTab === 'kol_tracker' ? '📊 KOL Posting Links Import' : '👥 KOL Dictionary Import'}
              </h2>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span>Valid: {Object.values(validationResults).filter(v => v.isValid).length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <span>Invalid: {Object.values(validationResults).filter(v => !v.isValid).length}</span>
                </div>
                {activeImportTab === 'kol_tracker' && (
                  <>
                    <div className="flex items-center gap-1">
                      <div className={`h-2 w-2 rounded-full ${isDataLoaded ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      <span>{isDataLoaded ? 'Matching Ready' : 'Loading...'}</span>
                    </div>
                    {Object.keys(matchResults).length > 0 && (
                      <>
                        <div className="flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-blue-500" />
                          <span>Matches: {Object.values(matchResults).filter(r => r.kolDbId).length}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-purple-500" />
                          <span>Avg Confidence: {Math.round(Object.values(matchResults).reduce((sum, r) => sum + r.confidence, 0) / Object.values(matchResults).length)}%</span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="h-[calc(100vh-200px)] overflow-hidden bg-card">
            {isUserNameLoaded ? (
              <SpreadsheetGridFixed
                columns={columns}
                data={gridData}
                onChange={handleGridDataChange}
                validationResults={validationResults}
                matchResults={matchResults}
                currentUserName={kolPicUserName}
                kolDbData={kolDbData}
                kolTrackerData={kolTrackerData}
                onManualMatch={handleManualMatch}
                onDeleteRow={handleDeleteRow}
                onValidationTrigger={handleValidationTrigger}
                selectedTable={activeImportTab}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading user information...
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
